import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { Observable, catchError, map, of } from 'rxjs';
import { IntegrationEventLog } from '../models/integration-eventlog.model';
import { PagingToken, FetchArgs } from 'src/app/shared/models/core';
import { IntegrationArtifact } from '../models/integration-artifact';
import { IntegrationLookup, ClientIntegrationType } from '../models/integration-lookup.model';
import { IntegrationVersionedConfigFile } from '../models/integration-config-file.model';
import { artifactNameParser } from '../integration-dashboard/state/artifact-name-parser';
import {
    ClientIntegrationIdentifier,
    ClientIntegrationVersionIdentifier,
    IntegrationNewVersionResponse
} from '../models/client-integration-identifier';
import { IntegrationEventLogDetails } from '../models/integration-event-log-details';
import {
    IntegrationStatusHealth,
    IntegrationStatusHealthSummaryFilter
} from '../models/integration-status-health.model';

import {
    SetPublishedVersionRequest,
    SetPublishedVersionResponse,
    PublishedVersionResponse
} from '../models/integration/published-version.model';
import { IntegrationRecordLogsDetails } from '../models/integration-record-log-details.model';
import { PublishedFilenames } from '../models/integration/published-filenames.model';

@Injectable({
    providedIn: 'root'
})
export class IntegrationManagerService {
    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {}

    getIntegrationEventLogs(args: FetchArgs): Observable<PagingToken<IntegrationEventLog[]>> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/event-logs`;
        return this._httpClient.post<PagingToken<IntegrationEventLog[]>>(url, args);
    }

    downloadIntegrationArtifact(args: IntegrationArtifact): Observable<any> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/download-artifact/${args.backupType}?filePath=${args.filePath}`;
        return this._httpClient.get(url, { observe: 'response', responseType: 'arraybuffer' });
    }

    getIntegrationMappingsLookup(): Observable<IntegrationLookup> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration`;
        return this._httpClient.get<ClientIntegrationType>(url).pipe(
            map((integrations) => {
                return {
                    clientIntegrationType: integrations
                };
            })
        );
    }

    downloadFile(res: HttpResponse<any>, artifact: IntegrationArtifact): Observable<boolean> {
        const blob = new Blob([res.body], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;

        // append url to element and trigger click
        downloadLink.setAttribute('download', artifactNameParser(artifact));
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // clean up
        document.body.removeChild(downloadLink);
        return of(true);
    }

    runV1Integration(config: ClientIntegrationIdentifier): Observable<boolean> {
        const url = `${this._settings.coreAPIUrl}/integrations/v1/client-integration/manual-trigger`;
        return this._httpClient
            .post<any>(url, config, {
                headers: { 'triggering-source': 'manual' }
            })
            .pipe(
                map(() => true),
                catchError(() => of(false))
            );
    }

    getIntegrationV1Metadata(): Observable<ClientIntegrationIdentifier[]> {
        const url = `${this._settings.coreAPIUrl}/integrations/v1/client-integration/metadata`;
        return this._httpClient.get<ClientIntegrationIdentifier[]>(url).pipe(catchError(() => of([])));
    }

    runV2Integration(config: ClientIntegrationIdentifier): Observable<boolean> {
        const url = `${this._settings.coreAPIUrl}/integrations/v2/client-integration/manual-trigger`;
        return this._httpClient
            .post<any>(url, config, {
                headers: { 'triggering-source': 'manual' }
            })
            .pipe(
                map(() => true),
                catchError(() => of(false))
            );
    }

    getIntegrationEventLogDetails(eventLogId: string): Observable<IntegrationEventLogDetails> {
        const url = `${this._settings.coreAPIUrl}/integrations/v2/manage-integration/event-logs/${eventLogId}`;
        return this._httpClient.get<IntegrationEventLogDetails>(url);
    }

    getIntegrationStatusHealth(filter: IntegrationStatusHealthSummaryFilter): Observable<IntegrationStatusHealth> {
        const url = `${this._settings.coreAPIUrl}/integrations/event-logs/metrics/status-health?clientName=${filter.clientName}&dateRange=${filter.dateRange}`;
        return this._httpClient.get<IntegrationStatusHealth>(url);
    }

    // manage versioning APIs
    getIntegrationVersions(identifier: ClientIntegrationVersionIdentifier): Observable<string[]> {
        const fromArchived = identifier?.fromArchived ? `&fromArchived=${identifier.fromArchived}` : '';
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/version-folders?clientName=${identifier.clientName}&integrationType=${identifier.integrationType}${fromArchived}`;
        return this._httpClient.get<string[]>(url);
    }

    createVersion(identifier: ClientIntegrationVersionIdentifier): Observable<IntegrationNewVersionResponse> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/create-version`;
        return this._httpClient.post<IntegrationNewVersionResponse>(url, identifier);
    }

    getIntegrationVersionedFiles(identifier: ClientIntegrationVersionIdentifier): Observable<string[]> {
        const fromArchived = identifier?.fromArchived ? `&fromArchived=${identifier.fromArchived}` : '';
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/versioned-files?clientName=${identifier.clientName}&integrationType=${identifier.integrationType}&version=${identifier.version}${fromArchived}`;
        return this._httpClient.get<string[]>(url);
    }

    getConfigVersionedFileContent(config: IntegrationVersionedConfigFile): Observable<string> {
        const fromArchived = config?.fromArchived ? `&fromArchived=${config.fromArchived}` : '';
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/versioned-file-content/${config.filename}?clientName=${config.clientName}&integrationType=${config.integrationType}&version=${config.version}${fromArchived}`;
        return this._httpClient.get(url, { responseType: 'text' });
    }

    saveVersionedConfigFile(config: IntegrationVersionedConfigFile): Observable<boolean> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/save-versioned-file`;
        return this._httpClient
            .post(url, config, { observe: 'response' })
            .pipe(map((response) => response.status === 204));
    }

    deleteVersionedConfigFile(config: IntegrationVersionedConfigFile): Observable<boolean> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/delete-versioned-file`;
        return this._httpClient
            .delete(url, { observe: 'response', body: config })
            .pipe(map((response) => response.status === 204));
    }

    setPublishedVersion(request: SetPublishedVersionRequest): Observable<SetPublishedVersionResponse> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/set-published-version`;
        return this._httpClient.post<SetPublishedVersionResponse>(url, request);
    }

    getAllPublishedVersions(identifier: ClientIntegrationIdentifier): Observable<PublishedVersionResponse[]> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/published-versions?clientName=${identifier.clientName}&integrationType=${identifier.integrationType}`;
        return this._httpClient.get<PublishedVersionResponse[]>(url);
    }

    getIntegrationRecordLogs(eventLogId: string, recordKey: string): Observable<IntegrationRecordLogsDetails> {
        const url =
            `${this._settings.coreAPIUrl}integrations/v2/manage-integration/event-logs/` +
            `${eventLogId}/records/${encodeURIComponent(recordKey)}`;
        return this._httpClient.get<IntegrationRecordLogsDetails>(url);
    }

    getIntegrationPublishedFilenames(identifier: ClientIntegrationVersionIdentifier): Observable<PublishedFilenames> {
        const url = `${this._settings.coreAPIUrl}integrations/v2/manage-integration/published-filenames?clientName=${identifier.clientName}&integrationType=${identifier.integrationType}`;
        return this._httpClient.get<PublishedFilenames>(url);
    }

    runDOPsPipeline(pipelineName: string): Observable<string> {
        const url = `${this._settings.coreAPIUrl}integrations/dops/run-pipeline`;
        return this._httpClient.post<string>(url, { integration: pipelineName });
    }
}
